// The dashboard sync API's shapes (contract: apiv2/dashboard_sync.openapi.yaml, x-contract-version 1).
// Readers ignore properties they do not know and show an unknown code as it is (the contract's
// versioning rule).

export interface SyncAccount {
    /** What to send as `account`; null for the default account. */
    account: string | null;
    label: string;
    is_default: boolean;
}

export interface SyncIntegration {
    integration: string;
    label: string;
    accounts: SyncAccount[];
    /** A product list to pick from and import. */
    products: boolean;
    /** Auxiliary information to read. */
    aux: boolean;
    /** A booking sync can be started from the dashboard (read as false when absent). */
    bookings?: boolean;
}

/** 0 pending, 1 running, 2 completed, 3 failed, 4 cancelled, 5 awaiting approval. */
export type TaskState = 0 | 1 | 2 | 3 | 4 | 5;

export interface SyncTask {
    task_id: string;
    task_type: string;
    task_label: string;
    state: TaskState;
}

export interface SyncOptions {
    /** `sync_bookings` (`ops.sync_bookings`): start a booking sync; read as false when absent. */
    permissions: { sync_products: boolean; sync_aux: boolean; sync_bookings?: boolean };
    integrations: SyncIntegration[];
    /** `bookings`: the newest booking sync (`sync_bookings`, `sync_bookings_with_approval`, the dry run `fetch_bookings`). */
    latest: { products: SyncTask | null; aux: SyncTask | null; bookings?: SyncTask | null };
}

export interface SyncProduct {
    id: string;
    name: string;
    facets: { label: string; value: string }[];
    tours: { id: number; name: string }[];
}

export interface SyncCatalogue {
    integration: string;
    account: string | null;
    fetched_ts: string;
    truncated: boolean;
    count: number;
    blocked: string | null;
    products: SyncProduct[];
}

export type SyncCatalogueLoad =
    | { status: 'ready'; catalogue: SyncCatalogue }
    | { status: 'pending'; task_id: string; state: TaskState; adopted: boolean };

/** The fields of a background-task row the page reads (`GET /v2/edit/bg_tasks/{id}/`). */
export interface BackgroundTaskRow {
    id: string;
    task_type: string;
    task_label: string;
    state: TaskState;
    state_display: string;
    progress: { current?: number | null; total?: number | null; pct?: number | null; step?: string } | null;
    error_message: string | null;
    can_cancel: boolean;
    can_approve: boolean;
}

export const ACTIVE_STATES: TaskState[] = [0, 1, 5];
export const isActive = (state: number | null | undefined) => state != null && (ACTIVE_STATES as number[]).includes(state);
